import { Loader2 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";

interface RecipeEditActionBarProps {
  dirty: boolean;
  isSaving?: boolean;
  onSave: () => void;
  onCancel: () => void;
}

/**
 * Save and Cancel, pinned to the bottom of the editor (T-707).
 *
 * The recipe form runs longer than a screen, and its only Save lived in the
 * page header — scroll down to the yield and there was no way to save what you
 * had just typed. This bar stays put, and says whether there is anything in it
 * to save.
 *
 * Cancel is the way out of edit mode, so it stays available whether or not
 * anything has been typed; only Save has nothing to do on an untouched form.
 */
export function RecipeEditActionBar({
  dirty,
  isSaving = false,
  onSave,
  onCancel,
}: RecipeEditActionBarProps) {
  const { t } = useLingui();

  return (
    <div
      role="region"
      aria-label={t`Save changes`}
      className="sticky bottom-0 z-30 flex items-center justify-between gap-3 border-t border-(--color-border) bg-(--color-surface-page) px-6 py-3"
    >
      <p
        className="text-sm text-(--color-text-secondary)"
        aria-live="polite"
      >
        {dirty ? t`Unsaved changes` : t`All changes saved`}
      </p>

      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          className="h-11 rounded-[0.625rem] border-(--color-border) px-5 text-base font-medium text-(--color-text-primary)"
          onClick={onCancel}
          disabled={isSaving}
        >
          {t`Cancel`}
        </Button>
        <Button
          type="button"
          className="h-11 gap-2 rounded-[0.625rem] bg-(--color-action) px-5 text-base font-medium text-(--color-action-fg) hover:bg-(--color-action)/90"
          onClick={onSave}
          disabled={!dirty || isSaving}
        >
          {isSaving && <Loader2 className="size-4 animate-spin" />}
          {t`Save`}
        </Button>
      </div>
    </div>
  );
}
